class VideoCarousel extends HTMLElement {
  #abortController = new AbortController();
  #activeSlide = null;
  #activeSlideVisible = false;
  #slideObserver = null;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    const signal = this.#abortController.signal;
    this.addEventListener('slideshow:select', this.#onSlideSelect, { signal });
    this.addEventListener('media:started-playing', this.#onMediaStarted, { signal });
    document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });
    this.#reducedMotion.addEventListener('change', this.#onReducedMotionChange, { signal });

    const scroller = this.querySelector('slideshow-slides');
    if (scroller) {
      this.#slideObserver = new IntersectionObserver(this.#onSlideIntersection, { root: scroller, threshold: 0.7 });
    }

    requestAnimationFrame(() => this.#setActiveSlide(this.querySelector('slideshow-slide[aria-hidden="false"]')));
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#slideObserver?.disconnect();
    this.#pauseAll();
  }

  #onSlideSelect = (event) => this.#setActiveSlide(event.detail.slide);

  #onMediaStarted = (event) => {
    const media = event.detail.resource;
    if (!this.contains(media)) return;
    this.#pauseAll(media);
  };

  #onVisibilityChange = () => {
    if (document.hidden) this.#pauseAll();
    else this.#playActiveIfEligible();
  };

  #onReducedMotionChange = () => {
    if (this.#reducedMotion.matches) this.#pauseAll();
    else this.#playActiveIfEligible();
  };

  #onSlideIntersection = (entries) => {
    const entry = entries.find((candidate) => candidate.target === this.#activeSlide);
    if (!entry) return;
    this.#activeSlideVisible = entry.isIntersecting;
    if (!entry.isIntersecting) this.#pauseAll();
    else this.#playActiveIfEligible();
  };

  #setActiveSlide(slide) {
    if (!(slide instanceof HTMLElement)) return;
    this.#pauseAll();
    this.#activeSlide = slide;
    this.#activeSlideVisible = false;
    this.#slideObserver?.disconnect();
    this.#slideObserver?.observe(slide);
  }

  #playActiveIfEligible() {
    if (this.dataset.playbackPolicy !== 'active' || document.hidden || this.#reducedMotion.matches || !this.#activeSlide || !this.#activeSlideVisible) return;
    const media = this.#activeSlide.querySelector('deferred-media');
    if (!(media instanceof HTMLElement)) return;

    media.loadContent?.(false);
    requestAnimationFrame(() => this.#playMedia(media));
  }

  #playMedia(media) {
    const video = media.querySelector('video');
    if (video) {
      video.muted = true;
      video.playsInline = true;
      video.play().catch(() => {});
      return;
    }

    const iframe = media.querySelector('iframe');
    if (!(iframe instanceof HTMLIFrameElement)) return;
    iframe.contentWindow?.postMessage(
      iframe.classList.contains('js-youtube')
        ? '{"event":"command","func":"playVideo","args":""}'
        : '{"method":"play"}',
      '*'
    );
  }

  #pauseAll(except = null) {
    for (const media of this.querySelectorAll('deferred-media')) {
      if (media === except) continue;
      media.pauseMedia?.();
      const iframe = media.querySelector('iframe');
      if (iframe instanceof HTMLIFrameElement) {
        iframe.contentWindow?.postMessage(
          iframe.classList.contains('js-youtube')
            ? '{"event":"command","func":"pauseVideo","args":""}'
            : '{"method":"pause"}',
          '*'
        );
      }
    }
  }
}

if (!customElements.get('video-carousel')) {
  customElements.define('video-carousel', VideoCarousel);
}
